/**
 * La copia local de lo que aún no está guardado.
 *
 * El autoguardado va cada 30 segundos. Si en medio se cierra la pestaña, se
 * cae la conexión o el editor falla y se queda en blanco, lo hecho desde el
 * último guardado se perdía. Esto lo copia en el navegador (IndexedDB) poco
 * después de cada cambio, mientras haya algo sin guardar, y lo borra en cuanto
 * todo está a salvo en el servidor. Al volver a abrir el proyecto, si queda una
 * copia, se ofrece recuperarla.
 *
 * Vive fuera de React a propósito: el store de zustand sobrevive a un fallo de
 * render, así que la pantalla de error todavía puede volcar la copia con lo que
 * había en el lienzo justo antes de romperse.
 */
import type { Page } from '../store/editor.store'
import { useEditorStore } from '../store/editor.store'
import { getBaseline, hasUnsavedChanges } from '../features/autosave/baseline'

export type LocalDraft = {
  projectId: string
  /** Cuándo se copió, en ms. */
  savedAt: number
  pages: Page[]
  projectContent: unknown
  projectTheme: unknown
  projectName?: string | undefined
}

const DB_NAME = 'veltica-drafts'
const STORE = 'drafts'
/** Una copia de hace más de una semana ya no se ofrece: lo guardado después manda. */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const WRITE_DELAY_MS = 1500

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null)
      const request = indexedDB.open(DB_NAME, 1)
      request.onupgradeneeded = () => request.result.createObjectStore(STORE)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => resolve(null)
      request.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function run<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  const db = await openDb()
  if (!db) return null
  return new Promise((resolve) => {
    try {
      const request = action(db.transaction(STORE, mode).objectStore(STORE))
      request.onsuccess = () => resolve(request.result ?? null)
      request.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

export async function readLocalDraft(projectId: string): Promise<LocalDraft | null> {
  const draft = await run<LocalDraft>('readonly', (store) => store.get(projectId) as IDBRequest<LocalDraft>)
  if (!draft || draft.projectId !== projectId) return null
  if (Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
    void discardLocalDraft(projectId)
    return null
  }
  return draft
}

export async function discardLocalDraft(projectId: string): Promise<void> {
  await run('readwrite', (store) => store.delete(projectId))
}

let active: { projectId: string; name: () => string | null } | null = null
/**
 * Retenida: la copia de la vez anterior espera a que se decida si se recupera.
 *
 * Mientras tanto no se toca. Sin esto, el primer guardado al abrir —o el
 * «Ver lo guardado» de un choque, que recarga— la borraba o la pisaba con lo
 * recién abierto, y los cambios que se iban a ofrecer desaparecían antes de
 * poder elegir.
 */
let held = false

/** Retiene la copia guardada (o la suelta) mientras se decide si se recupera. */
export function holdLocalDraft(next: boolean): void {
  held = next
}
let timer: ReturnType<typeof setTimeout> | null = null
let unsubscribe: (() => void) | null = null

function pendingNow(): boolean {
  if (!active) return false
  const state = useEditorStore.getState()
  return hasUnsavedChanges({ pages: state.pages, content: state.projectContent, theme: state.projectTheme, name: active.name() })
}

/**
 * Copia ya lo que hay, o borra la copia si ya no hay nada pendiente.
 *
 * Es lo que llama la pantalla de error y lo que corre tras cada guardado
 * correcto (para no dejar una copia vieja que se ofrecería al volver).
 */
export async function flushLocalDraft(force = false): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null }
  if (!active || (held && !force) || !getBaseline().ready) return
  const { projectId } = active
  if (!pendingNow()) {
    await discardLocalDraft(projectId)
    return
  }
  const state = useEditorStore.getState()
  const draft: LocalDraft = {
    projectId,
    savedAt: Date.now(),
    pages: state.pages,
    projectContent: state.projectContent,
    projectTheme: state.projectTheme,
    projectName: active.name() ?? undefined,
  }
  await run('readwrite', (store) => store.put(draft, projectId))
}

export function scheduleLocalDraftFlush(): void {
  if (!active || held || !getBaseline().ready) return
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => { void flushLocalDraft() }, WRITE_DELAY_MS)
}

/**
 * Empieza a copiar los cambios del proyecto abierto. Se llama una vez que el
 * proyecto cargó y tiene línea de base; devuelve cómo pararlo.
 */
export function startLocalDraft(projectId: string, name: () => string | null): () => void {
  stopLocalDraft()
  active = { projectId, name }
  unsubscribe = useEditorStore.subscribe((state, previous) => {
    if (state.pages === previous.pages && state.projectContent === previous.projectContent && state.projectTheme === previous.projectTheme) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => { void flushLocalDraft() }, WRITE_DELAY_MS)
  })
  return stopLocalDraft
}

export function stopLocalDraft(): void {
  if (timer) { clearTimeout(timer); timer = null }
  held = false
  unsubscribe?.()
  unsubscribe = null
  active = null
}

/** Si la copia trae algo distinto de lo que se acaba de abrir. */
export function draftDiffers(draft: LocalDraft, loaded: { pages: Page[]; projectContent: unknown; projectTheme: unknown; projectName?: string | null }): boolean {
  return JSON.stringify(draft.pages) !== JSON.stringify(loaded.pages)
    || JSON.stringify(draft.projectContent) !== JSON.stringify(loaded.projectContent)
    || JSON.stringify(draft.projectTheme) !== JSON.stringify(loaded.projectTheme)
    || (draft.projectName !== undefined && loaded.projectName !== undefined && draft.projectName !== loaded.projectName)
}
